import { useMemo } from "react";
import _ from "lodash";
import { usePerformanceReportQuery } from "../../App/Queries/ReportingQueries";
import { MessageDeliveryType } from "../../App/Enums/Message";
import { IPerformanceReport } from "../../App/Typings/Reports/CampaignEmailPerformanceReport";

export type EmailPerformanceReportState = ReturnType<typeof useEmailPerformanceReportState>;

/**
 * Gets the Campaign email performance report data based on the provided parameters
 * @param startDate The start date of the report
 * @param endDate The end date of the report
 * @param campaignId The campaign id to filter the report by
 * @param enablePreviousReport Whether or not to enable the previous report comparison query
 * @returns
 */
export const useEmailPerformanceReportState = (startDate: Date, endDate: Date, groupId?: string) => {
  const numDaysInReport = useMemo(() => {
    return Math.round((endDate.valueOf() - startDate.valueOf()) / (1000 * 60 * 60 * 24));
  }, [endDate, startDate]);

  const { data: reportData, isLoading } = usePerformanceReportQuery(
    startDate,
    endDate,
    MessageDeliveryType.Email,
    groupId,
  );

  const {
    prevStartDate,
    prevEndDate,
    prevDeliveredCount,
    prevOpenCount,
    prevClickCount,
    prevUnsubscribeCount,
    isLoadingPrevReport,
    deliveredPercentChange,
    openPercentChange,
    clickPercentChange,
    unsubscribePercentChange,
  } = usePreviousReportData(startDate, endDate, groupId, reportData);

  return {
    getters: {
      isLoading,
      isLoadingPrevReport,
      numDaysInReport,
      reportData,
      prevStartDate,
      prevEndDate,
      prevDeliveredCount,
      prevOpenCount,
      prevClickCount,
      prevUnsubscribeCount,
      deliveredPercentChange,
      openPercentChange,
      clickPercentChange,
      unsubscribePercentChange,
    },
  } as const;
};

const usePreviousReportData = (
  startDate: Date,
  endDate: Date,
  groupId?: string,
  reportData?: IPerformanceReport,
) => {
  const { prevStartDate, prevEndDate } = getPreviousTimespan(startDate, endDate);
  const { data: prevReportData, isLoading: isLoadingPrevReport } = usePerformanceReportQuery(
    prevStartDate,
    prevEndDate,
    MessageDeliveryType.Email,
    groupId,
    reportData !== undefined,
  );

  const openPercentChange = getPercentChange("openCount", prevReportData, reportData);
  const prevDeliveredCount =
    prevReportData?.dataPoints?.reduce((acc, curr) => acc + curr.deliveredCount, 0) ?? 0;
  const prevOpenCount = prevReportData?.dataPoints?.reduce((acc, curr) => acc + curr.openCount, 0) ?? 0;
  const deliveredPercentChange = getPercentChange("deliveredCount", prevReportData, reportData);
  const clickPercentChange = getPercentChange("clickCount", prevReportData, reportData);
  const prevClickCount = prevReportData?.dataPoints?.reduce((acc, curr) => acc + curr.clickCount, 0) ?? 0;
  const unsubscribePercentChange = getPercentChange("unsubscribeCount", prevReportData, reportData);
  const prevUnsubscribeCount =
    prevReportData?.dataPoints?.reduce((acc, curr) => acc + curr.unsubscribeCount, 0) ?? 0;

  return {
    prevStartDate,
    prevEndDate,
    prevDeliveredCount,
    prevOpenCount,
    prevClickCount,
    prevUnsubscribeCount,
    isLoadingPrevReport,
    deliveredPercentChange,
    openPercentChange,
    clickPercentChange,
    unsubscribePercentChange,
  };
};

export function getPercentChange(dataKey: string, prev?: IPerformanceReport, current?: IPerformanceReport) {
  if (!prev || !current) {
    return 0;
  }
  const previousCount = prev.dataPoints?.reduce((acc, curr) => acc + curr[dataKey], 0) ?? 0;
  const currentCount = current.dataPoints?.reduce((acc, curr) => acc + curr[dataKey], 0) ?? 0;
  const percentChange = !previousCount
    ? currentCount * 100
    : ((currentCount - previousCount) / previousCount) * 100;
  return _.round(percentChange, 2);
}

function getPreviousTimespan(startDate: Date, endDate: Date) {
  const timespanInMilliseconds = endDate.valueOf() - startDate.valueOf();
  const prevStartDate = new Date(startDate.valueOf() - timespanInMilliseconds);
  const prevEndDate = new Date(endDate.valueOf() - timespanInMilliseconds);
  return { prevStartDate, prevEndDate };
}
